<template>
  <div>
    <h1 class="pb-4" v-text="$t('program.orderedPrograms')" />
    <h2
      v-text="
        $t('program.approveDifferentSchoolRequestsToAddNewProgramsToTheSchool')
      "
      class="pb-12"
    />
    <pagination-actions-table
      introjs="actions-table"
      disable-sort
      class="mb-10"
      :loading="loading"
      :headers="headers"
      :items="readableProgramOrders"
      :actions-title="$t('userActions.denyOrApprove')"
      :action-two-icon-tooltip="`${$t('userActions.deny')} / ${$t(
        'userActions.cancel'
      )}`"
      @action-one-click="onApproveClick"
      @action-two-click="onRejectClick"
      @paginate="getOrders"
    />
    <modal-approve v-model="isModalOpen" @approve="approveOrder">
      {{
        this.$t(
          "program.approvingTheRequestWillAllowTheSchoolAndProgramToCollaborate"
        )
      }}
    </modal-approve>
    <form-dialog
      v-model="isDenyFormDialogActive"
      :title="$tc('general.additionalInfo', 0)"
      :subtitle="`${$t(
        'program.attention!ThisActionWillCancelTheProgramEventsInTheSchoolIfExist'
      )}.`"
      :input-fields="rejectDialogFields"
      @save="denyOrder"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex"
import debounce from "lodash/debounce"
import store from "@/vuex/store"
import Api from "@/api"
import Utils from "@/helpers/utils"
import { SERVER } from "@/helpers/constants/constants"
import PaginationActionsTable from "@/components/Tables/PaginationActionsTable"
import ModalApprove from "@/components/ModalApprove"
import FormDialog from "@/components/FormDialog"
import introjsSubscribeMixin from "@/mixins/introJs/introjsSubscribeMixin"

export default {
  name: "GatekeeperProgramOrders",
  components: { PaginationActionsTable, ModalApprove, FormDialog },
  mixins: [introjsSubscribeMixin],
  async beforeRouteEnter(to, from, next) {
    await store.dispatch("gatekeeperProgram/getProgramOrders")
    next()
  },
  computed: {
    ...mapState("gatekeeperProgram", ["programOrders"]),
    readableProgramOrders() {
      return this.programOrders.map(order => {
        const readableCreatedAt = Utils.ApiStringToReadableDate(order.createdAt)
        const readableUpdatedAt = Utils.ApiStringToReadableDate(order.updatedAt)
        const readableStatus = this.$t(
          `status.${Object.keys(SERVER.programOrderStatus).find(
            key => SERVER.programOrderStatus[key] === order.status
          )}`
        )
        return {
          ...order,
          readableCreatedAt,
          readableUpdatedAt,
          readableStatus,
        }
      })
    },
  },
  data() {
    return {
      loading: false,
      orderToApprove: null,
      orderToReject: null,
      isModalOpen: false,
      isDenyFormDialogActive: false,
      rejectDialogFields: [
        {
          name: "statusReason",
          rule: "required",
          label: this.$t("general.reasonForDenyOrCancellation"),
          value: "",
        },
      ],
      headers: [
        { text: this.$t("program.programName"), value: "activityName" },
        { text: this.$t("general.schoolName"), value: "schoolName" },
        { text: this.$t("time.creationDate"), value: "readableCreatedAt" },
        { text: this.$t("time.updateDate"), value: "readableUpdatedAt" },
        { text: this.$t("general.status"), value: "readableStatus" },
        {
          text: this.$t("general.reasonForDenyOrCancellation"),
          value: "statusReason",
        },
      ],
    }
  },
  methods: {
    ...mapActions("gatekeeperProgram", [
      "getProgramOrders",
      "updateProgramOrder",
    ]),
    ...mapActions("snackbar", ["showMessage"]),
    async getOrders() {
      this.loading = true
      await this.getProgramOrders({ override: true, usePagination: true })
      this.loading = false
    },
    onApproveClick: debounce(
      async function (order) {
        if (order.status === SERVER.programOrderStatus.approved) {
          return this.showMessage(this.$t("errors.programIsAlreadyApproved"))
        }
        this.orderToApprove = order
        this.isModalOpen = true
      },
      500,
      { leading: true, trailing: false }
    ),

    onRejectClick: debounce(
      async function (order) {
        if (
          [
            SERVER.programOrderStatus.cancelled,
            SERVER.programOrderStatus.denied,
          ].includes(order.status)
        ) {
          return this.showMessage(
            this.$t("errors.cantRejectAlreadyCancelledOrDeniedPrograms")
          )
        }
        this.orderToReject = order
        this.isDenyFormDialogActive = true
      },
      500,
      {
        leading: true,
        trailing: false,
      }
    ),

    async approveOrder() {
      try {
        await this.updateProgramOrder({
          slug: this.orderToApprove.slug,
          data: {
            status: SERVER.programOrderStatus.approved,
            statusReason: "",
          },
        })
        this.showMessage(this.$t("success.programSuccessfullyApproved"))
      } catch (err) {
        this.showMessage(Api.utils.parseResponseError(err))
      }
    },

    async denyOrder({ statusReason }) {
      try {
        await this.updateProgramOrder({
          slug: this.orderToReject.slug,
          data: {
            status: SERVER.programOrderStatus.denied,
            statusReason: statusReason,
          },
        })
        this.showMessage(this.$t("success.programDenied"))
      } catch (err) {
        this.showMessage(Api.utils.parseResponseError(err))
      }
    },
  },
}
</script>
